import { capitalize } from 'underscore.string';
import { dimensionalUnits, weightUnits } from 'utils/localization/types';
import convert from 'convert-units';
import { sluggify } from 'utils/localization/utils';

const PALLET_MAX_HEIGHT_INCHES = 98;

export function validateLoadDetails(job) {
  const { weight, pallets, boxes, full_truckload: fullTruckload, t } = job;

  const palletLoad = pallets.length > 0 ? pallets[0].quantity : false;
  const loadExists = palletLoad || boxes.quantity || fullTruckload;

  const errors = {};

  if (!loadExists) {
    errors.load = t('PROVIDE_PALLETS_OR_BOX_INFO');
  }

  if (!weight) {
    errors.weight = t('ENTER_WEIGHT');
  }

  let palletErrors = {};
  if (!fullTruckload && pallets.length > 0) {
    const pallet = pallets[0];
    palletErrors = validateLoadItem({
      ...pallet,
      type: 'pallets',
    });
  }

  const boxesAdded = Object.prototype.hasOwnProperty.call(boxes, 'quantity');
  let boxErrors = {};
  if (!fullTruckload && boxesAdded) {
    boxErrors = validateLoadItem({
      ...boxes,
      type: 'boxes',
    });
  }

  return {
    ...errors,
    ...palletErrors,
    ...boxErrors,
  };
}

export function validateLoadDetailsV2(job) {
  const { loads, full_truckload, weight, t, weight_units } = job;
  const loadQty = loads.reduce((total, load) => {
    return total + parseInt(load.quantity, 10);
  }, 0);
  const loadExists = loadQty || full_truckload;
  const errors = {};

  if (!loadExists) {
    errors.load = t('PROVIDE_PALLETS_OR_BOX_INFO');
  }

  if (!weight) {
    errors.weight = t('PROVIDE_SHIPMENT_WEIGHT');
  }

  if (!weight_units || !weightUnits.includes(weight_units)) {
    errors.weight_units = t('PROVIDE_VALID_SHIPMENT_WEIGHT_UNIT');
  }

  let loadItemErrors = [];
  if (!full_truckload && loads.length > 0) {
    loadItemErrors = loads.map((load) => {
      return validateLoadItemV2(load, t);
    });
  }

  return { ...errors, loadItemErrors };
}

export function validateLoadItem(load, t) {
  const {
    quantity,
    width,
    height,
    length,
    type,
    weight_units,
    dimensional_units,
  } = load;

  const errors = {};

  if (!type || type === '') {
    errors.type = [t('SET_LOAD_TYPE')];
    return errors;
  }

  const typeLabel = t(sluggify(type));

  if (!quantity || parseInt(quantity, 10) < 0) {
    errors.quantity = [t('SET_QUANTITY', { typeLabel })];
  }

  if (!length || parseInt(length, 10) < 0) {
    errors.length = [t('SET_LENGTH', { typeLabel })];
  }

  if (!width || parseInt(width, 10) < 0) {
    errors.width = [t('SET_WEIGHT', { typeLabel })];
  }

  if (!(weightUnits || []).includes(weight_units)) {
    errors.weight_units = [t('SET_WEIGHT_UNITS', { typeLabel })];
  }

  if (!(dimensionalUnits || []).includes(dimensional_units)) {
    errors.dimensional_units = [t('SET_DIMENTIONAL_UNITS', { typeLabel })];
  }

  if (!height || parseInt(height, 10) < 0) {
    errors.height = [t('SET_HEIGHT', { typeLabel })];
  } else {
    const maxHeight = getMaxHeightForSelectedMeasurementUnit(dimensional_units);
    if (type === 'pallets' && parseInt(height, 10) > maxHeight) {
      const maxHeightWithMeasurementUnit = `${maxHeight} ${dimensional_units}`;
      errors.height = (errors.height || []).concat(
        t('PALLET_HEIGHT_ERROR', {
          palletMaxHeight: maxHeightWithMeasurementUnit,
        }),
      );
    }
  }

  return errors;
}

function getMaxHeightForSelectedMeasurementUnit(measurementUnit) {
  const convertedMaxHeight = convert(PALLET_MAX_HEIGHT_INCHES)
    .from('in')
    .to(measurementUnit);
  const convertedMaxHeightWithFixedLength = convertedMaxHeight.toFixed(3);
  return Number(convertedMaxHeightWithFixedLength);
}

export function validateLoadItemV2(load, t) {
  const { weight, type } = load;

  const errors = validateLoadItem(load, t);

  const typeLabel = t(sluggify(type));

  if (typeof weight !== 'undefined' && (!weight || parseInt(weight, 10) <= 0)) {
    errors.weight = [t('SET_WEIGHT', { typeLabel })];
  }

  return errors;
}

const hasValue = (refnum) => {
  return refnum && refnum !== '';
};

export function validateShipperReferenceNumbers(shipper, refnums, t) {
  const errors = {};

  if (!shipper?.require_tracking_id) {
    return errors;
  }

  if (
    !hasValue(refnums.tracking_id) &&
    !hasValue(refnums.mo_number) &&
    !hasValue(refnums.house_bill_number)
  ) {
    errors.referenceNumbers = t('PROVIDE_SHIPPER_REF_NUM');
  }

  return errors;
}
